import { defineStore } from 'pinia';
import { type Ref, ref, computed } from 'vue';
import type { NotificationTag } from '@/types/NotificationPreferences';
import { Address, Country, User } from '@/types/User.ts';
import { Api } from '@/utils/api.ts';
import { MfaMethod } from '@/types/MfaMethod.ts';
import { UserResponse } from '@/types/Responses/UserResponse.ts';
import { MfaStatusResponse } from '@/types/Responses/MfaStatusResponse.ts';
import { MfaRequestSudo } from '@/types/Responses/MfaRequestSudo.ts';
import { TotpRecoveryResponse } from '@/types/Responses/TotpRecoveryResponse.ts';
import { useAuthStore } from '@/stores/auth.ts';
import { useRouter } from 'vue-router';

export const useUserStore = defineStore('user', () => {
	const user: Ref<User> = ref({
		id: '',
		email: [],
		phone: [],
		fridom_user_id: null,
		user_id: null,
		customer_user_id: null,
		firstname: '',
		lastname: '',
		birthdate: '',
		language: 'en',
		password_status: 0,
		last_password_change: null,
		mfa_forced: false,
		mfa_customer: false,
		has_sso: false,
		sso_only: false,
		partner_user_id: null,
	});
	const recoveryKeys: Ref<TotpRecoveryResponse[]> = ref([]);
	const address: Ref<Address | null> = ref(null);
	const addressLoaded: Ref<boolean> = ref(false);
	const countries: Ref<Country[]> = ref([]);
	const countriesLoaded: Ref<boolean> = ref(false);
	const mfaMethods: Ref<MfaMethod[]> = ref([]);
	const payload: Ref<string> = ref(''); // Holds the current payload in case of MFA request
	const currentMfaMethod: Ref<string> = ref('');
	const authStore = useAuthStore();
	const router = useRouter();

	const refreshUser = async () => {
		try {
			const response = await Api.get('/user');

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const responseBody = await (response.json() as Promise<UserResponse>);
			user.value = responseBody.data;

			if (user.value.mfa_forced) {
				sessionStorage.setItem('mfa_forced', '1');
			} else if (!user.value.mfa_forced && sessionStorage.getItem('mfa_forced')) {
				sessionStorage.removeItem('mfa_forced');
			}
			user.value.email = user.value.email.sort((a, b) => Number(b.default) - Number(a.default));
			user.value.phone = user.value.phone.sort((a, b) => Number(b.default) - Number(a.default));

			if (responseBody.data.password_status === 1 || responseBody.data.password_status === 2) {
				await Promise.reject('weak');
				return;
			}

			return responseBody;
		} catch (error) {
			if (error === 'weak') {
				throw error;
			}
			console.error('Fetch Error:', error);
		}
	};

	const refreshAddress = async () => {
		try {
			const response = await Api.getFridom('/user/address');

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const responseBody = await (response.json() as Promise<Address | null>);
			address.value = responseBody?.street ? responseBody : null;
			addressLoaded.value = true;
		} catch (error) {
			console.error('Fetch address error:', error);
		}
	};

	const ensureAddress = async () => {
		if (!addressLoaded.value) {
			await refreshAddress();
		}
	};

	const refreshCountries = async (lang: string) => {
		try {
			const response = await Api.getFridom(`/${lang}/api/countries`);

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			const responseBody = await (response.json() as Promise<Array<{ country_id: number; isocode: string; name: string }>>);
			countries.value = responseBody.map((country) => ({ id: country.country_id, isocode: country.isocode, name: country.name }));
			countriesLoaded.value = true;
		} catch (error) {
			console.error('Fetch countries error:', error);
		}
	};

	const ensureCountries = async (lang: string) => {
		if (!countriesLoaded.value) {
			await refreshCountries(lang);
		}
	};

	const refreshMfa = async (includeAll: boolean = true, token?: string) => {
		try {
			const response = await Api.get('/mfa', { includeAll }, token, true);
			if (!response.ok) {
				throw new Error('Network response was not ok');
			}

			mfaMethods.value = await (response.json() as Promise<MfaMethod[]>);
		} catch (error) {
			if (error === '401' && !token) {
				authStore.logout();
			}
		}
	};

	const clean = () => {
		user.value = {
			id: '',
			email: [],
			phone: [],
			fridom_user_id: null,
			user_id: null,
			customer_user_id: null,
			firstname: '',
			lastname: '',
			birthdate: '',
			language: 'en',
			password_status: 0,
			last_password_change: null,
			mfa_forced: false,
			mfa_customer: false,
			has_sso: false,
			sso_only: false,
			partner_user_id: null,
		};
		address.value = null;
		addressLoaded.value = false;
		countries.value = [];
		countriesLoaded.value = false;
		mfaMethods.value = [];
		payload.value = '';
		currentMfaMethod.value = '';
	};

	const isSudomode = async (single?: boolean, token?: string): Promise<boolean> => {
		try {
			const response = await Api.get('/mfa/status', {}, token);
			const data = await (response.json() as Promise<MfaStatusResponse>);

			if (mfaMethods.value.length === 0) {
				await refreshMfa(true, token);
			}
			// // This specific tweak is necessary to force MFA flow if not set
			currentMfaMethod.value = data.need_card ? 'card' : '';
			if (single && data.single !== undefined) {
				return data.single;
			}
			return data.sudo;
		} catch {
			return Promise.reject(false);
		}
	};

	const requestSudomode = async (single?: boolean, mfaId?: number, token?: string, payload?: string, channel?: string): Promise<void> => {
		const response = await Api.get(
			'/mfa/sudo',
			{
				single: single ? '1' : '0',
				...(mfaId !== undefined && { mfaId: mfaId.toString() }),
				...(payload !== undefined && { payload }),
				...(channel !== undefined && { channel }),
			},
			token,
		);
		const data = await (response.json() as Promise<MfaRequestSudo>);

		await setRequestedSudomode(data.payload, data.method);
	};

	const setRequestedSudomode = async (newPayload: string, newMethod: string): Promise<void> => {
		payload.value = newPayload;
		currentMfaMethod.value = newMethod;
	};

	const activateSudomode = async (code: string, isTrustedDevice: boolean, storedPayload?: string, token?: string): Promise<boolean> => {
		const response = await Api.post(
			'/mfa/sudo',
			{ payload: storedPayload ?? payload.value, code, isTrustedDevice: isTrustedDevice },
			undefined,
			token,
		);
		const data = await response.json();
		if (data.access_token) {
			await authStore.loginByToken(data.access_token);
		}
		return await isSudomode(undefined, token);
	};

	const isMfaGranted = async (redirect: string): Promise<boolean> => {
		await refreshMfa();
		if (!(await isSudomode())) {
			router.push({ name: 'mfa-methods', query: { redirect } });
			return false;
		}

		return true;
	};

	const deleteMfa = async (mfaId: number, target?: string, force = false) => {
		await Api.delete(`/mfa/${mfaId}`, { target: mfaId < 0 ? target : undefined, force });
	};

	const refreshRecoveryKeys = async () => {
		try {
			const response = await Api.get('/mfa/recovery-keys');
			recoveryKeys.value = await (response.json() as Promise<TotpRecoveryResponse[]>);
		} catch {
			recoveryKeys.value = [];
		}
	};

	const regenerateRecovery = async () => {
		await Api.post(`/mfa/regenerate-recovery`, undefined).then(() => {
			refreshRecoveryKeys();
		});
	};

	const switchPrimary = async (mfaId: number): Promise<void> => {
		Api.post(`/mfa/switch-primary/${mfaId}`, undefined).then(() => {
			refreshUser();
		});
	};

	/**
	 * Computed property that returns the notification tags based on user roles.
	 * Used to filter Novu preferences by relevant sections.
	 */
	const notificationTags = computed((): NotificationTag[] => {
		const tags: NotificationTag[] = ['uzzer']; // Global is always included
		if (user.value.user_id) tags.push('beneficiary');
		if (user.value.customer_user_id) tags.push('employer');
		if (user.value.partner_user_id) tags.push('partner');
		if (user.value.fridom_user_id) tags.push('parking');
		return tags;
	});

	return {
		mfaMethods,
		currentMfaMethod,
		user,
		recoveryKeys,
		address,
		countries,
		notificationTags,
		clean,
		refreshUser,
		refreshAddress,
		ensureAddress,
		refreshCountries,
		ensureCountries,
		switchPrimary,
		refreshMfa,
		deleteMfa,
		refreshRecoveryKeys,
		regenerateRecovery,
		isMfa: isSudomode,
		requestMfa: requestSudomode,
		setRequestedMfa: setRequestedSudomode,
		activateMfa: activateSudomode,
		isMfaGranted,
	};
});
